import { Inject, Injectable } from '@angular/core'
import { select, Store } from '@ngrx/store'
import {
  BaseSiteService,
  SiteContextActions,
  SiteContextConfig,
  StateWithSiteContext,
  WindowRef
} from '@spartacus/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { filter, map, take, tap } from 'rxjs/operators'
import { EbBaseSite, EbSiteContextFallbackValues } from '../interfaces/eb-site-context.interface'
import { EbSiteContextSelectors } from '../store/selectors'

@Injectable({ providedIn: 'root' })
export class EbBaseSiteService extends BaseSiteService {
  activeBaseSite$ = new BehaviorSubject<string>(undefined)
  private readonly activeContext$ = new BehaviorSubject<EbBaseSite>(undefined)

  constructor(
    protected store: Store<StateWithSiteContext>,
    protected config: SiteContextConfig,
    protected winRef: WindowRef,
    @Inject('Window') protected window
  ) {
    super(store, config)
  }

  getActiveContext(): Observable<EbBaseSite> {
    return this.activeContext$
  }

  /**
   * Represents the current baseSite uid.
   */
  getActive(): Observable<string> {
    return this.store.pipe(
      select(EbSiteContextSelectors.getActiveBaseSite),
      tap(() => {
        // fallback is always german basesite
        if (this.getCountryFromUrlWithoutFallback() === '') {
          this.setActive(EbSiteContextFallbackValues.BASESITE)
        }
      }),
      filter(active => Boolean(active)),
      tap(active => this.activeBaseSite$.next(active))
    )
  }

  /**
   * Get all base sites data
   */
  getAll(): Observable<EbBaseSite[]> {
    return this.store.pipe(
      select(EbSiteContextSelectors.getAllBaseSites),
      map(sites => sites as EbBaseSite[]),
      tap(sites => {
        if (!sites) {
          this.store.dispatch(new SiteContextActions.LoadBaseSites())
        } else {
          this.activeContext$.next(sites.find(site => site.uid === this.activeBaseSite$.value))
        }
      }),
      filter(sites => Boolean(sites))
    )
  }

  setActive(baseSite: string): void {
    this.store
      .pipe(select(EbSiteContextSelectors.getActiveBaseSite), take(1))
      .subscribe(activeBaseSite => {
        if (baseSite && activeBaseSite !== baseSite) {
          if (activeBaseSite) {
            this.winRef.localStorage.removeItem(`spartacus⚿${baseSite}⚿cart`)
          }
          this.store.dispatch(new SiteContextActions.SetActiveBaseSite(baseSite))
          this.activeBaseSite$.next(baseSite)
          // check if we are in checkout - then go back to cart
          if (this.winRef.location.href.includes('/checkout/')) {
            const baseUrl = this.winRef.location.href.split('/checkout/')[0]
            this.window.open(`${baseUrl}/cart`, '_self')
          }
        }
      })
  }

  getCountryFromUrlWithoutFallback(): string {
    const urlParts = this.winRef.location.href.split('/')
    // verify that first parameter is existing and a country (upper case) and not a language(lower case)
    if (!urlParts[3] || urlParts[3] === urlParts[3].toUpperCase()) {
      return urlParts[3]
    } else if (urlParts[3]) {
      return urlParts[3].toUpperCase()
    } else {
      // fallback
      return ''
    }
  }
}
